/**
 * core/intelligence/meetingProcessor.ts
 *
 * AI Meeting Processing — extracts action items, decisions, and topics from
 * meeting notes stored as monday.com item updates (comments).
 *
 * This module is intentionally deterministic and runs fully client-side.
 * It uses pattern-matching heuristics rather than a remote LLM call so that
 * it works without an additional API key and has no latency cost.
 *
 * For each meeting item the processor:
 *   1. Concatenates all update bodies into a single transcript.
 *   2. Extracts action items via regex patterns (e.g. "Action:", "@mention will…").
 *   3. Extracts decision statements (e.g. "Decided:", "We agreed…").
 *   4. Produces a bullet-point summary capped at 5 lines.
 *   5. Derives a lightweight sentiment signal (positive / neutral / negative).
 */

import type {
  MondayItem,
  MondayNotification,
  MeetingProcessingResult,
  ExtractedActionItem,
} from '@core/types';

// ─── Patterns ─────────────────────────────────────────────────────────────────

/** Lines that start with these prefixes are likely action items */
const ACTION_PREFIXES = [
  /^action[:：]\s*/i,
  /^todo[:：]\s*/i,
  /^task[:：]\s*/i,
  /^follow.?up[:：]\s*/i,
  /^ai[:：]\s*/i, // common meeting abbreviation for "Action Item"
];

/** Lines that contain assignment language */
const ASSIGNMENT_PATTERNS = [
  /\b(?:will|to|must|should|needs? to|has to)\b.{3,60}(?:by|before|on|until)/i,
  /@\w+ (?:will|to|should|must)\b/i,
];

/** Lines that indicate a decision was made */
const DECISION_PATTERNS = [
  /^decided[:：]\s*/i,
  /^decision[:：]\s*/i,
  /\bwe (?:agreed|decided|resolved|confirmed)\b/i,
  /\bapproved\b.{0,40}$/i,
  /\brejected\b.{0,40}$/i,
];

/** Simple positive / negative word sets for sentiment */
const POSITIVE_WORDS = new Set([
  'approved', 'agreed', 'resolved', 'excellent', 'great', 'success', 'done',
  'completed', 'achieved', 'positive', 'progress', 'good', 'happy', 'confirmed',
]);
const NEGATIVE_WORDS = new Set([
  'blocked', 'issue', 'problem', 'delay', 'risk', 'concern', 'escalate',
  'failed', 'overdue', 'stuck', 'rejected', 'postponed', 'cancelled', 'urgent',
]);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
    .trim();
}

function extractAssignee(line: string): string | undefined {
  const mentionMatch = line.match(/@(\w+)/);
  if (mentionMatch) return mentionMatch[1];
  return undefined;
}

function extractDueDate(line: string): string | undefined {
  // Simple ISO date or common phrase patterns
  const isoMatch = line.match(/\b(\d{4}-\d{2}-\d{2})\b/);
  if (isoMatch) return isoMatch[1];
  const relativeMatch = line.match(
    /\b(by|before|on|until)\s+(monday|tuesday|wednesday|thursday|friday|end of (?:week|month|day))\b/i,
  );
  if (relativeMatch) return relativeMatch[0];
  return undefined;
}

function scoreSentiment(text: string): 'positive' | 'neutral' | 'negative' {
  const words = text.toLowerCase().split(/\W+/);
  let positiveCount = 0;
  let negativeCount = 0;
  for (const w of words) {
    if (POSITIVE_WORDS.has(w)) positiveCount++;
    if (NEGATIVE_WORDS.has(w)) negativeCount++;
  }
  if (positiveCount > negativeCount + 1) return 'positive';
  if (negativeCount > positiveCount + 1) return 'negative';
  return 'neutral';
}

function buildSummary(lines: string[], maxLines = 5): string {
  // Take the first non-empty, non-action, non-decision lines as summary bullets
  const bullets = lines
    .map((l) => l.trim())
    .filter((l) => l.length > 10 && l.length < 200)
    .filter((l) => !ACTION_PREFIXES.some((p) => p.test(l)))
    .filter((l) => !DECISION_PATTERNS.some((p) => p.test(l)))
    .slice(0, maxLines);

  return bullets.map((b) => `• ${b}`).join('\n');
}

// ─── Main export ──────────────────────────────────────────────────────────────

/**
 * Process a meeting item and its update bodies into a structured result.
 *
 * @param item    - The monday.com item representing the meeting
 * @param updates - Update objects fetched from the item (plain text / HTML bodies)
 */
export function processMeeting(
  item: MondayItem,
  updates: Array<Pick<MondayNotification, 'id'> & { text: string }>,
): MeetingProcessingResult {
  // Concatenate all update bodies
  const transcript = updates.map((u) => stripHtml(u.text)).join('\n\n');
  const lines = transcript
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

  const actionItems: ExtractedActionItem[] = [];
  const decisions: string[] = [];
  const topics: string[] = [];

  for (const line of lines) {
    // ── Action items ──────────────────────────────────────────────────────
    const isExplicitAction = ACTION_PREFIXES.some((p) => p.test(line));
    const isImplicitAction =
      !isExplicitAction && ASSIGNMENT_PATTERNS.some((p) => p.test(line));

    if (isExplicitAction || isImplicitAction) {
      // Strip the prefix label before storing
      let text = line;
      for (const p of ACTION_PREFIXES) {
        text = text.replace(p, '');
      }
      actionItems.push({
        text: text.trim(),
        assignee: extractAssignee(line),
        dueDate: extractDueDate(line),
        priority: line.match(/\burgent\b|\bcritical\b/i) ? 'High' : undefined,
      });
      continue;
    }

    // ── Decisions ──────────────────────────────────────────────────────────
    if (DECISION_PATTERNS.some((p) => p.test(line))) {
      let text = line;
      for (const p of DECISION_PATTERNS) {
        text = text.replace(p, '');
      }
      decisions.push(text.trim() || line.trim());
      continue;
    }

    // ── Topics — lines that look like headings or agenda items ────────────
    if (
      /^[A-Z]/.test(line) &&
      line.length < 80 &&
      !line.endsWith(',') &&
      (line.endsWith(':') || line.split(' ').length <= 6)
    ) {
      topics.push(line.replace(/:$/, '').trim());
    }
  }

  const summary = buildSummary(lines);
  const sentiment = scoreSentiment(transcript);

  return {
    meetingId: item.id,
    summary,
    actionItems,
    decisions,
    topics: [...new Set(topics)].slice(0, 10), // deduplicate, cap at 10
    sentiment,
    processedAt: new Date().toISOString(),
  };
}
